import ReactGA from 'react-ga4';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-9HT32G0P63';

interface AnalyticsConfig {
  debug?: boolean;
  testMode?: boolean;
}

class AnalyticsService {
  private isInitialized = false;
  private config: AnalyticsConfig;

  constructor(config: AnalyticsConfig = {}) {
    this.config = config;
  }

  /**
   * Initialize Google Analytics
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('Google Analytics already initialized');
      return;
    }

    try {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        testMode: this.config.testMode || false,
        gtagOptions: {
          // Enhanced privacy settings
          anonymize_ip: true,
          cookie_flags: 'SameSite=None;Secure',
        },
      });

      this.isInitialized = true;
      console.log('Google Analytics initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Track page views
   */
  trackPageView(path: string, title?: string, additionalData?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn('Google Analytics not initialized');
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
        ...additionalData,
      });

      if (this.config.debug) {
        console.log('Page view tracked:', { path, title, additionalData });
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom events
   */
  trackEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.isInitialized) {
      console.warn('Google Analytics not initialized');
      return;
    }

    try {
      ReactGA.event({
        action,
        category,
        label,
        value,
      });

      if (this.config.debug) {
        console.log('Event tracked:', { action, category, label, value });
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track blog-specific events
   */
  trackBlogEvent(action: string, postSlug?: string, category?: string): void {
    this.trackEvent(action, category || 'Blog', postSlug);
  }

  /**
   * Track course-related events
   */
  trackCourseEvent(action: string, courseId?: string, category?: string): void {
    this.trackEvent(action, category || 'Course', courseId);
  }

  /**
   * Track contact form events
   */
  trackContactEvent(action: string, formType?: string): void {
    this.trackEvent(action, 'Contact', formType);
  }

  /**
   * Check if analytics is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }
}

// Create singleton instance
export const analytics = new AnalyticsService({
  debug: process.env.NODE_ENV === 'development',
  testMode: process.env.NODE_ENV === 'test',
});

// Export the service class for testing
export { AnalyticsService };

// Export measurement ID for reference
export { GA_MEASUREMENT_ID };